/**
 * Send Email to Farmer Dialog
 * 
 * Allows sending personalized emails to farmers using EMAIL templates.
 */

import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Button,
  Select,
  toast
} from '@etherisc/ui-kit';
import { XIcon, MailIcon } from 'lucide-react';
import type { Farmer } from '../../types/farmer';

interface EmailTemplate {
  id: string;
  name: string;
  description?: string;
}

interface SendEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  farmer: Farmer;
}

export function SendEmailDialog({
  isOpen,
  onClose,
  farmer
}: SendEmailDialogProps) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadEmailTemplates();
    }
  }, [isOpen]);

  const loadEmailTemplates = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/api/document-templates?type=EMAIL&status=ACTIVE', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load EMAIL templates');
      }

      const data = await response.json();
      setTemplates(data.data.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    if (!farmer.email) {
      toast.error('Farmer has no email address');
      return;
    }

    try {
      setSending(true);

      const template = templates.find(t => t.id === selectedTemplate);

      // Queue notification
      const response = await fetch('/api/notifications/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth-token')}`,
          'Content-Type': 'application/json',
          'X-Idempotency-Key': `farmer-email-${farmer.id}-${Date.now()}`
        },
        body: JSON.stringify({
          channel: 'EMAIL',
          to: [{
            email: farmer.email,
            name: farmer.name
          }],
          template: {
            name: template?.name || selectedTemplate,
            version: 'v1'
          },
          variables: {
            farmerName: farmer.name,
            farmerCode: farmer.farmerCode,
            phoneNumber: farmer.phone || 'Not provided',
            county: farmer.county || 'Not provided',
            ward: farmer.ward || 'Not specified'
          },
          channelMetadata: {
            subject: `Message for ${farmer.name}`,
            replyTo: 'support@myubuntu.etherisc.com'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      toast.success('Email queued for sending', {
        description: `Will be sent to ${farmer.email}`
      });

      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-md mx-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <MailIcon size={20} />
                Send Email to {farmer.name}
              </h2>
              <Button variant="outline" size="sm" onClick={onClose}>
                <XIcon size={16} />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Farmer Details */}
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm text-gray-600">Recipient</p>
                <p className="font-medium">{farmer.email || 'No email address'}</p>
              </div>

              {/* Template Selection */}
              <div>
                <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Template
                </label>
                {templates.length > 0 ? (
                  <Select
                    id="template"
                    value={selectedTemplate}
                    onValueChange={setSelectedTemplate}
                    options={templates.map(t => ({
                      value: t.id,
                      label: `${t.name}${t.description ? ` - ${t.description}` : ''}`
                    }))}
                    placeholder="Select a template..."
                  />
                ) : (
                  <p className="text-sm text-gray-500 py-2">Loading templates...</p>
                )}
              </div>

              {loading && (
                <p className="text-sm text-gray-500 text-center">Loading templates...</p>
              )}

              {!loading && templates.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-sm text-yellow-800">
                    No EMAIL templates found. Create one in Document Templates first.
                  </p>
                </div>
              )}

              {selectedTemplate && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-blue-900 font-medium">Variables available:</p>
                  <ul className="text-xs text-blue-800 mt-1 space-y-1">
                    <li>• farmerName, farmerCode, phoneNumber, county, ward</li>
                  </ul>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 mt-6">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSend} 
                disabled={!selectedTemplate || !farmer.email || sending}
              >
                <MailIcon size={16} className="mr-2" />
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

